/**
 * BioEditor — kompakter Markdown-Editor mit Toolbar (Bold/Italic/Liste/Emoji).
 * Wird in `OnboardingPage.js` und `EditProfilePage.js` verwendet, damit Bios
 * dieselben Formatierungs-Möglichkeiten haben wie Artikel-Beschreibungen.
 */
import { useMemo, useState } from "react";

const EMOJIS = ["😊","😍","🥰","😎","😉","😜","🥳","🤔","🙌","👍","👏","❤️","💚","💙","💜","🖤","🤍","✨","🌸","🌟","🔥","💫","🌿","🌈","🎁","👗","👠","👜","💃","🕺","💋","💌","💖","💝","👑","🌹","💐","☕","🍷","🍾","🎉","🎈","🎂","🥂","🌍","✌️","🤝","😇","🥺","😘"];

function wrap(el, prefix, suffix, current, setValue) {
  if (!el) { setValue(current + prefix + suffix); return; }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const before = current.slice(0, start);
  const selected = current.slice(start, end) || "Text";
  const after = current.slice(end);
  setValue(before + prefix + selected + suffix + after);
  setTimeout(() => {
    el.focus();
    el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
  }, 0);
}

function prependLine(el, prefix, current, setValue) {
  if (!el) { setValue(prefix + current); return; }
  const pos = el.selectionStart;
  const before = current.slice(0, pos);
  const lineStart = before.lastIndexOf("\n") + 1;
  const next = current.slice(0, lineStart) + prefix + current.slice(lineStart);
  setValue(next);
  setTimeout(() => { el.focus(); el.setSelectionRange(pos + prefix.length, pos + prefix.length); }, 0);
}

function insertAt(el, text, current, setValue) {
  if (!el) { setValue(current + text); return; }
  const pos = el.selectionStart;
  const next = current.slice(0, pos) + text + current.slice(pos);
  setValue(next);
  setTimeout(() => { el.focus(); el.setSelectionRange(pos + text.length, pos + text.length); }, 0);
}

function ToolbarBtn({ children, onClick, label, testId }) {
  return (
    <button type="button" onClick={onClick} title={label} data-testid={testId}
      className="h-8 px-2 text-xs bg-white border border-[#E5E5E5] hover:bg-[#F5F5F5]">
      {children}
    </button>
  );
}

export default function BioEditor({ value, onChange, maxLength = 500, id = "bio-editor", placeholder = "Kurz über dich…", testId = "bio-editor" }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const el = useMemo(() => (typeof document !== "undefined" ? document.getElementById(id) : null), [id, showEmoji]);

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1 mb-1">
        <ToolbarBtn label="Fett" testId="bio-fmt-bold" onClick={() => wrap(document.getElementById(id), "**", "**", value, onChange)}>
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn label="Kursiv" testId="bio-fmt-italic" onClick={() => wrap(document.getElementById(id), "*", "*", value, onChange)}>
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn label="Liste" testId="bio-fmt-list" onClick={() => prependLine(document.getElementById(id), "- ", value, onChange)}>
          • Liste
        </ToolbarBtn>
        <ToolbarBtn label="Nummeriert" testId="bio-fmt-numlist" onClick={() => prependLine(document.getElementById(id), "1. ", value, onChange)}>
          1. Liste
        </ToolbarBtn>
        <div className="relative">
          <button type="button" onClick={() => setShowEmoji((v) => !v)}
            data-testid="bio-fmt-emoji-btn"
            className="h-8 px-2 text-xs bg-white border border-[#E5E5E5] hover:bg-[#F5F5F5]">
            😊 Emoji
          </button>
          {showEmoji && (
            <div data-testid="bio-emoji-picker"
              className="absolute z-20 top-full mt-1 left-0 w-64 max-h-40 overflow-y-auto p-2 bg-white border border-[#E5E5E5] shadow-lg grid grid-cols-8 gap-1">
              {EMOJIS.map((e) => (
                <button key={e} type="button"
                  onClick={() => { insertAt(document.getElementById(id), e, value, onChange); setShowEmoji(false); }}
                  className="h-7 hover:bg-[#F5F5F5] text-lg">{e}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      <textarea id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        className="w-full h-28 px-4 py-3 border border-[#E5E5E5] bg-white text-sm resize-y focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] font-mono leading-relaxed" />
      <p className="text-right text-[10px] text-[#CCC]">{(value || "").length}/{maxLength}</p>
    </div>
  );
}
